import { gqlClient } from '../../components/AppProvider';
import {
  CommunicationType,
  MutationStatusCode,
  PaymentRequestConnection,
  PaymentRequestOrder,
  DisputeConnection,
  DisputeOrder,
  DocumentTokenInput,
  Person,
  PaymentOrder,
  PaymentConnection,
} from '../../gql-types.generated';
import { mutationRefund } from '../../gql/MutationRefund';
import { mutationUploadDocument } from '../../gql/MutationUploadDocument';
// import { mutationStatement } from '../../gql/MutationStatement';
import { mutationUpsertPaymentRequest } from '../../gql/MutationUpsertPaymentRequest';
import { queryAccountRequirements } from '../../gql/QueryAdditionalVerifications';
import { queryPaymentRequestConnection } from '../../gql/QueryPaymentRequestConnection';
import { queryDisputeConnection } from '../../gql/QueryDisputeConnection';
import { queryPaymentConnection } from '../../gql/QueryPaymentConnection';
import { AppDispatch, AppThunk } from '../../store';
import { decrementRequestsInFlight, incrementRequestsInFlight } from '../app/AppSlice';
import {
  capturePaymentRequestStatus,
  captureRefundSuccess,
  fetchAccountRequirementsSuccess,
  fetchPaymentRequestListSuccess,
  fetchDisputeListSuccess,
  captureSearchError,
  captureDisputeListError,
  captureInvolvedUsersError,
  captureRefundError,
  captureRemindError,
  captureVerificationsError,
  captureAccountRequirementsError,
  uploadDocumentSuccess,
  uploadDocumentFailure,
  captureSendPaymentRequestError,
  clearSendPaymentRequestError,
  captureReminderStatus,
  captureVerificationStatus,
  fetchInvolvedUsersSuccess,
  fetchIsLoadingDisputes,
  fetchIsLoadingPaymentRequests,
  fetchIsLoadingPayments,
  fetchRefundIdempotencyKey,
  fetchPaymentListSuccess,
  capturePaymentListError,
  fetchRefundErrorMessage,
} from './HomeSlice';
import { mutationSubmitVerification } from '../../gql/MutationSubmitVerification';
import { queryPersonList } from '../../gql/QueryPersonList';

export const fetchInvolvedUsers = () => async (dispatch: AppDispatch) => {
  dispatch(incrementRequestsInFlight());
  try {
    const involvedUsers = await queryPersonList(gqlClient);
    if (involvedUsers) {
      dispatch(fetchInvolvedUsersSuccess(involvedUsers as Person[]));
    }
    dispatch(decrementRequestsInFlight());
  } catch (e) {
    console.error(e);
    dispatch(captureInvolvedUsersError(e));
    dispatch(decrementRequestsInFlight());
  }
};

export const fetchAccountRequirements = () => async (dispatch: AppDispatch) => {
  dispatch(incrementRequestsInFlight());
  try {
    const accountRequirements = await queryAccountRequirements(gqlClient);
    if (accountRequirements) {
      dispatch(fetchAccountRequirementsSuccess(accountRequirements));
      dispatch(fetchInvolvedUsers());
    }
    dispatch(decrementRequestsInFlight());
  } catch (e) {
    console.error(e);
    dispatch(captureAccountRequirementsError(e));
    dispatch(decrementRequestsInFlight());
  }
};

export const fetchPaymentRequestList = (
  after: string | undefined,
  pageSize: number | undefined,
  orderBy: PaymentRequestOrder,
  queryString: string | undefined,
) => async (dispatch: AppDispatch) => {
  try {
    // NOTE: The last n items does nothing, so we'll pass in 0
    const last = 0;
    const before = undefined;
    const first = pageSize || 25;
    dispatch(fetchIsLoadingPaymentRequests(true));
    const paymentRequestList = await queryPaymentRequestConnection(gqlClient, after, before, first, last, orderBy, queryString);
    if (paymentRequestList) {
      dispatch(fetchPaymentRequestListSuccess(paymentRequestList as PaymentRequestConnection));
      dispatch(fetchIsLoadingPaymentRequests(false));
    }
  } catch (e) {
    console.error(e);
    dispatch(captureSearchError(e));
    dispatch(fetchIsLoadingPaymentRequests(false));
  }
};

export const fetchDisputeList = (after: string | undefined, pageSize: number | undefined, orderBy: DisputeOrder) => async (
  dispatch: AppDispatch,
) => {
  try {
    const last = 0;
    const before = undefined;
    const first = pageSize || 25;
    dispatch(fetchIsLoadingDisputes(true));
    const disputeList = await queryDisputeConnection(gqlClient, after, before, first, last, orderBy);
    if (disputeList) {
      dispatch(fetchDisputeListSuccess(disputeList as DisputeConnection));
      dispatch(fetchIsLoadingDisputes(false));
    }
  } catch (e) {
    console.error(e);
    dispatch(captureDisputeListError(e));
    dispatch(fetchIsLoadingDisputes(false));
  }
};

export const fetchPaymentList = (
  after: string | undefined,
  pageSize: number | undefined,
  orderBy: PaymentOrder,
  queryString: string | undefined,
) => async (dispatch: AppDispatch) => {
  try {
    const last = 0;
    const before = undefined;
    const first = pageSize || 25;
    dispatch(fetchIsLoadingPayments(true));
    const paymentList = await queryPaymentConnection(gqlClient, after, before, first, last, orderBy, queryString);
    if (paymentList) {
      dispatch(fetchPaymentListSuccess(paymentList as PaymentConnection));
      dispatch(fetchIsLoadingPayments(false));
    }
  } catch (e) {
    dispatch(captureSearchError(e));
    dispatch(capturePaymentListError(e));
    dispatch(fetchIsLoadingPayments(false));
  }
};

export const upsertPaymentRequest = (
  referenceId: string,
  amount: number,
  invoiceRef: string,
  email?: string,
  phoneNumber?: string,
): AppThunk => async (dispatch: AppDispatch): Promise<void> => {
  try {
    dispatch(clearSendPaymentRequestError());
    // The email must always be provided, if the phone number is provided to, we need to send SMS and Email
    const type = phoneNumber ? CommunicationType.EmailAndSms : CommunicationType.Email;
    const paymentRequest = await mutationUpsertPaymentRequest(gqlClient, referenceId, type, amount, invoiceRef, email, phoneNumber);
    if (paymentRequest) {
      dispatch(capturePaymentRequestStatus(paymentRequest));
      if (paymentRequest.code === MutationStatusCode.Error) {
        dispatch(captureSendPaymentRequestError(paymentRequest.message || ''));
      } else {
        dispatch(clearSendPaymentRequestError());
      }
    }
    if (!paymentRequest) throw new Error('[HomeActions] Payment Request not sent');
  } catch (e) {
    console.error(e);
    dispatch(captureSendPaymentRequestError(e.message));
  }
};

export const uploadDocument = (file: File): AppThunk => async (dispatch: AppDispatch): Promise<void> => {
  try {
    const uploadRequest = await mutationUploadDocument(gqlClient, file);
    if (!uploadRequest) {
      dispatch(uploadDocumentFailure());
      throw new Error('[HomeActions] Upload Document request not sent');
    }
    dispatch(uploadDocumentSuccess(uploadRequest.id));
  } catch (e) {
    dispatch(uploadDocumentFailure());
    console.error(e);
  }
};

export const refund = (
  refundIdempotencyKey: string,
  paymentId: string,
  refundReason: string,
  amount?: number | null,
): AppThunk => async (dispatch: AppDispatch): Promise<void> => {
  try {
    const refundRequest = await mutationRefund(gqlClient, refundIdempotencyKey, paymentId, refundReason, amount);
    if (refundRequest && refundRequest.code) {
      dispatch(captureRefundSuccess(refundRequest.code));
      dispatch(fetchRefundIdempotencyKey(undefined)); //reset the refund idempotency key now that the refund call is finished
      if (refundRequest.code === MutationStatusCode.Error) {
        dispatch(captureRefundError({ message: refundRequest.message } as Error));
        dispatch(fetchRefundErrorMessage(refundRequest.message));
      }
    }
    if (!refundRequest) throw new Error('[HomeActions] Payment Request not sent');
    console.log('refundRequest: ', refundRequest);
    //dispatch(decrementRequestsInFlight());
  } catch (e) {
    console.error(e);
    dispatch(captureRefundError(e));
    dispatch(fetchRefundIdempotencyKey(undefined));
    //dispatch(decrementRequestsInFlight());
  }
};

export const remindPaymentRequest = (
  referenceId: string,
  amount: number,
  id: string,
  invoiceId: string,
  email: string,
): AppThunk => async (dispatch: AppDispatch): Promise<void> => {
  try {
    const type = CommunicationType.Email;
    const paymentRequest = await mutationUpsertPaymentRequest(gqlClient, referenceId, type, amount, invoiceId, email, undefined, id);
    if (paymentRequest) {
      dispatch(captureReminderStatus(paymentRequest));
      if (paymentRequest.code === MutationStatusCode.Error) {
        dispatch(captureRemindError({ message: paymentRequest.message } as Error));
      }
    }
    if (!paymentRequest) throw new Error('[HomeActions] Payment Reminder not sent');
  } catch (e) {
    console.error(e);
    dispatch(captureRemindError(e));
  }
};

export const upsertVerifications = (documentTokens: DocumentTokenInput[]): AppThunk => async (
  dispatch: AppDispatch,
): Promise<void> => {
  try {
    const submitVerification = await mutationSubmitVerification(gqlClient, documentTokens);
    if (submitVerification) {
      dispatch(captureVerificationStatus(submitVerification));
      if (submitVerification.code === MutationStatusCode.Error) {
        dispatch(captureVerificationsError({ message: submitVerification.message } as Error));
      }
    }
    if (!submitVerification) throw new Error('[HomeActions] Verifications not submitted');
  } catch (e) {
    console.error(e);
    dispatch(captureVerificationsError(e));
  }
};

// export const downloadStatement = (startDate: Date, endDate: Date): AppThunk => async (dispatch: AppDispatch): Promise<void> => {
//   try {
//     const statement = await mutationStatement(gqlClient, startDate, endDate);
//     if (!statement) {
//       throw new Error('[HomeActions] Generate statement request not sent');
//     }
//   } catch (e) {
//     console.error(e);
//   }
// };
